import { MouseEvent, memo, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import translationKeys from '@translationKeys'

import AddCircleIcon from '@mui/icons-material/AddCircle'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle'
import SearchIcon from '@mui/icons-material/Search'
import { InputAdornment } from '@mui/material'
import AccordionSummary from '@mui/material/AccordionSummary'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { isSubstringOf } from '@shared/utils'
import { colors } from '@theme/variables'

import { FilterAccordion, FilterAccordionDetails, FilterBadge, FilterBox, FilterClearIcon } from '../../index.styles'
import { FILTER_LEVEL_TYPES, FacetItem } from '../../types'
import MultiLevelSelect from './MultiLevelSelect'
import MultiSelect from './MultiSelect'
import SingleSelect from './SingleSelect'
import { FilterSearchInput, ShowMoreWrapper } from './index.styles'

interface Props {
  data: FacetItem
  selectedValues: string[]
  onChange: (key: string, value: string, direction: boolean, multiple?: boolean) => void
  onClearSection: (key: string) => void
  onExpand: (key: string, value: boolean, nestedName?: string) => void
  expandedFilter: Record<string, boolean | string>
  filterName: string
  allOptionsExpanded?: boolean
  setExpandedAllOptions?: (value: boolean) => void
  searchString?: string
  setSearchString?: (value: string) => void
  limit?: number
}

const GenericFilterSelect = (props: Props) => {
  const {
    data: { type, key, name, values },
    selectedValues = [],
    onChange,
    onClearSection,
    onExpand,
    expandedFilter,
    filterName,
    searchString,
    setSearchString,
    allOptionsExpanded,
    setExpandedAllOptions,
    limit,
  } = props
  const searchStringIsSufficientForSearch = searchString && searchString.length > 1
  const { t } = useTranslation()

  const selectedOptions = useMemo(() => {
    const optionsMap = selectedValues.reduce<Record<string, Omit<FacetItem, 'key' | 'type'>>>((acc, value) => {
      const [parentFilterName, nestedFilterName] = value.split('::')

      let updatedValues = acc[parentFilterName]?.values
      if (nestedFilterName) {
        if (!updatedValues) {
          updatedValues = []
        }

        updatedValues.push({ name: nestedFilterName, total: 0 })
      }

      acc[parentFilterName] = {
        name: parentFilterName,
        total: 0,
        values: updatedValues,
      }
      return acc
    }, {})

    return Object.values(optionsMap)
  }, [selectedValues])

  const filteredOptions = useMemo(() => {
    const filteredValues = values.filter(
      value =>
        !searchStringIsSufficientForSearch ||
        isSubstringOf(value.name, searchString) ||
        value.values?.some(subValue => isSubstringOf(subValue.name, searchString)),
    )

    const preparedValues = filteredValues?.length ? filteredValues : selectedOptions

    const mappedValues = preparedValues
      .slice(0, limit && !allOptionsExpanded && !searchStringIsSufficientForSearch ? limit : filteredValues.length)
      .map(i => ({
        label: i.name,
        total: String(i.total),
        value: i.name,
        forceChecked: i.checked,
        options: i.values
          ?.filter(subValue => !searchStringIsSufficientForSearch || isSubstringOf(subValue.name, searchString))
          .map(j => ({
            label: j.name,
            total: String(j.total),
            value: j.name,
            forceChecked: i.checked,
          })),
      }))
    return mappedValues
  }, [values, searchString, allOptionsExpanded, selectedOptions])

  const showPlusMore = limit && values.length > limit && !searchStringIsSufficientForSearch
  const showSearch = limit && values.length > limit

  const handleFilterExpand = (e: MouseEvent<HTMLElement>, value: boolean, nestedName?: string) => {
    onExpand(key, value, nestedName)
  }

  const handleClearSection = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation()
    onClearSection(key)
  }

  const renderSelect = () => {
    if (type === FILTER_LEVEL_TYPES.SINGLE_SELECT) {
      return (
        <SingleSelect
          value={selectedValues[0] || null}
          label=""
          options={[{ value: null, label: 'None', total: '' }, ...filteredOptions]}
          onChange={(v: string) => onChange(key, v, Boolean(v || v === null), false)}
        />
      )
    }

    if (type === FILTER_LEVEL_TYPES.MULTI_SELECT) {
      return (
        <MultiSelect
          values={selectedValues}
          label=""
          options={filteredOptions}
          onChange={(v: string) => onChange(key, v, !selectedValues.includes(v))}
        />
      )
    }

    if (type === FILTER_LEVEL_TYPES.MULTI_SELECT_LEVEL) {
      return (
        <MultiLevelSelect
          values={selectedValues}
          label=""
          options={filteredOptions}
          onChange={(v: string) => onChange(key, v, !selectedValues.includes(v))}
          handleSelectExpand={handleFilterExpand}
          expandedFilter={expandedFilter}
          filterName={filterName}
        />
      )
    }

    return null
  }

  const isChildSelected = selectedValues?.length > 0

  return (
    <Box>
      <FilterAccordion disableGutters onChange={handleFilterExpand} expanded={!!expandedFilter?.[key]}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} data-testid="filter_accordion_summary">
          <FilterBox>
            <Typography variant="body2">
              {name}
              {!!isChildSelected && (
                <FilterBadge variant="dot" color="primary" data-testid={`${key.toLowerCase()}_filter_badge`} />
              )}
            </Typography>

            {!!isChildSelected && (
              <FilterClearIcon onClick={handleClearSection} color="primary" data-testid="filter_clear_section" />
            )}
          </FilterBox>
        </AccordionSummary>
        <FilterAccordionDetails>
          {showSearch && (
            <FilterSearchInput
              data-testid="generic-filter-search"
              value={searchString}
              type="text"
              variant="outlined"
              onChange={e => setSearchString?.(e?.target.value || '')}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon htmlColor={colors.bodyText} fontSize="inherit" />
                  </InputAdornment>
                ),
              }}
            />
          )}
          {renderSelect()}
          {searchString && !filteredOptions?.length && (
            <Typography data-testid="no-results" variant="body1">
              {t(translationKeys.SEARCH_NO_RESULTS)}
            </Typography>
          )}
          {showPlusMore &&
            (allOptionsExpanded ? (
              <ShowMoreWrapper data-testid="show-less" onClick={() => setExpandedAllOptions?.(false)}>
                {t(translationKeys.SEARCH_FILTER_SECTION_SEE_LESS)} <RemoveCircleIcon color="primary" />
              </ShowMoreWrapper>
            ) : (
              <ShowMoreWrapper data-testid="show-more" onClick={() => setExpandedAllOptions?.(true)}>
                {t(translationKeys.SEARCH_FILTER_SECTION_SEE_MORE)} <AddCircleIcon color="primary" />
              </ShowMoreWrapper>
            ))}
        </FilterAccordionDetails>
      </FilterAccordion>
    </Box>
  )
}

export default memo(GenericFilterSelect)
